import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ConfigurationPlan } from '../../../../../models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { Material } from '../../../../../models/configurators-2d/applications/workbench/materials/material';
import { WorkbenchService } from '../../../../../services/configurators-2d/applications/workbench.service';
import { Configurators2DComponent } from '../../../configurators-2d.component';
import { IWorkbenchPreview } from '../../preview/i-workbench-preview';
import { Color } from '../../../../../models/configurators-2d/applications/workbench/materials/color';
import { MaterialThickness } from '../../../../../models/configurators-2d/applications/workbench/materials/material-thickness';
import { WorkbenchConfiguratorItem } from '../../../configurator/applications/workbench-configurator-item';
import { ConfigurationGroup } from '../../../../../models/configurators-2d/applications/workbench/configurations/configuration-group';
import { MaterialTypeEnum } from '../../../../../enum/workbench/material-type-enum';
import { PlanRestriction } from '../../../../../models/configurators-2d/applications/workbench/constraints/plan-restriction';

/**
 * Affichage de la carte avec la sélection de la matière/épaisseur/couleur d'un groupe ou plan
 */
@Component({
  selector: 'ng-workbench-color-groups-plans',
  templateUrl: './color-groups-plans.component.html',
})
export class WorkbenchColorGroupsPlansComponent extends WorkbenchConfiguratorItem implements OnInit {
  @Input() collapseIsVisible: boolean;
  @Input() planOrGroup: ConfigurationGroup | ConfigurationPlan;
  @Input() materials: Material[];
  @Output() onPlanOrGroupSelected: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onMaterialChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  materialsWithRestrictions: Material[];

  // Aide pour les groupes pour savoir quels matériaux est sélectionné
  materialSelected: Material;
  materialThicknessSelected: MaterialThickness;
  MaterialTypeEnum = MaterialTypeEnum;
  colorSelected: Color;

  // Preview
  preview: IWorkbenchPreview;

  constructor(
    public workbenchService: WorkbenchService,
    configurators2DComponent: Configurators2DComponent
  ) {
    super(workbenchService);
    this.preview = configurators2DComponent.preview as IWorkbenchPreview;
  }

  ngOnInit(): void {
    const plans: ConfigurationPlan[] = [];
    // group
    if (this.planOrGroup instanceof ConfigurationGroup) {
      plans.push(...this.planOrGroup.workbenchConfigurationPlans);
    } else {
      // plan
      plans.push(this.planOrGroup);
    }

    // Récupère les restrictions des plans
    const promises = plans.map(plan =>
      this.service.getPlanRestrictions(plan).then(res => (plan.planRestrictions = res))
    );

    Promise.all(promises).then(() => {
      this.setMaterialsWithRestrictions();
      this.setSelectedModels();
    });
  }

  /**
   * Change la visibilité de la carte
   */
  toggleCollapse(): void {
    this.setSelectedModels();

    this.collapseIsVisible = !this.collapseIsVisible;
    this.onPlanOrGroupSelected.emit(this.collapseIsVisible);
  }

  /**
   * Récupère les matériaux et leurs restrictions
   */
  setMaterialsWithRestrictions(): void {
    const planRestrictions = this.getPlanRestrictions();

    this.materialsWithRestrictions = this.materials.filter(
      m =>
        m.materialThicknesses.some(mt => !planRestrictions.some(pr => pr?.restrictedThicknessIds.includes(mt.id))) &&
        m.colors.some(c => !planRestrictions.some(pr => pr?.restrictedColorIds.includes(c.id)))
    );
  }

  /**
   * Définie le matériau, l'épaisseur et la couleur sélectionnés
   * @constructor
   * @private
   */
  private setSelectedModels(): void {
    // Si les restrictions ne sont pas encore chargées (async), on n'efface pas les données utilisateur
    if (!this.materialsWithRestrictions) return;

    const plan = this.getConfigurationPlan();

    if (plan.material != null && this.materialsWithRestrictions.some(m => m?.id === plan?.material?.id)) {
      this.materialSelected = this.materialsWithRestrictions.find(m => m.id === plan.material.id);
    } else {
      this.materialSelected = null;
      plan.material = null;
      plan.materialId = null;
    }

    if (
      plan.materialThickness != null &&
      this.getMaterialThicknessWithRestrictions().some(mt => mt?.id === plan?.materialThickness?.id)
    ) {
      this.materialThicknessSelected = this.getMaterialThicknessWithRestrictions().find(
        mt => mt.id === plan.materialThickness.id
      );
    } else {
      this.materialThicknessSelected = null;
      plan.materialThicknessId = null;
      plan.materialThickness = null;
    }

    if (plan.color != null && this.getColorsWithRestrictions().some(c => c?.id === plan?.color?.id)) {
      this.colorSelected = this.getColorsWithRestrictions().find(mt => mt.id === plan.color.id);
    } else {
      this.colorSelected = null;
      plan.colorId = null;
      plan.color = null;
      this.preview.removePlanOrGroupDecor(this.planOrGroup);
    }
  }

  /**
   * Récupère les épaisseurs avec leurs restrictions
   */
  getMaterialThicknessWithRestrictions(): MaterialThickness[] {
    if (!this.materialSelected) return [];
    const planRestrictions = this.getPlanRestrictions();

    return (this.materials.find(m => m.id === this.materialSelected.id)?.materialThicknesses ?? [])
      .filter(mt => !planRestrictions.some(pr => pr?.restrictedThicknessIds.includes(mt.id)));
  }

  /**
   * Récupère les couleurs avec leurs restrictions
   */
  getColorsWithRestrictions(): Color[] {
    if (!this.materialSelected || !this.materialThicknessSelected) return [];
    const planRestrictions = this.getPlanRestrictions();

    return (this.materials.find(m => m.id === this.materialSelected.id)?.colors ?? [])
      .filter(
        c =>
          !planRestrictions.some(pr => pr.restrictedColorIds.includes(c.id)) &&
          !c.restrictedThicknessesIds.includes(this.materialThicknessSelected.id)
      );
  }

  /**
   * Récupère les couleurs avec leurs restrictions à afficher
   */
  getColorsWithRestrictionsToDisplay(): Color[] {
    return this.getColorsWithRestrictions().filter(c => c.disabled != true || this.colorSelected?.id === c.id);
  }

  /**
   * Sélection du matériau
   * @param material
   */
  async selectMaterial(material: Material): Promise<void> {
    const plan = this.getConfigurationPlan();

    // group
    if (this.planOrGroup instanceof ConfigurationGroup) {
      this.planOrGroup.workbenchConfigurationPlans.forEach(p => {
        p.materialId = material.id;
        p.material = material;
        p.materialThickness = null;
        p.materialThicknessId = null;
        p.color = null;
        p.colorId = null;
        this.resetFinishes(p);
      });
    } else {
      // plan
      plan.materialId = material.id;
      plan.material = material;
      plan.materialThickness = null;
      plan.materialThicknessId = null;
      plan.color = null;
      plan.colorId = null;
      this.resetFinishes(plan);
    }

    this.materialSelected = material;
    this.materialThicknessSelected = undefined;
    this.colorSelected = undefined;
    await this.preview.recreateEdgesCutawaysAndJambs();
    await this.preview.recreateSplashbacks();
    this.onMaterialChanged.emit(true);
  }

  /**
   * Sélection de l'épaisseur
   * @param materialThickness
   */
  async selectMaterialThickness(materialThickness: MaterialThickness): Promise<void> {
    // group
    if (this.planOrGroup instanceof ConfigurationGroup) {
      this.planOrGroup.workbenchConfigurationPlans.forEach(plan => {
        plan.materialThicknessId = materialThickness.id;
        plan.materialThickness = materialThickness;
        plan.color = null;
        plan.colorId = null;
        this.resetFinishes(plan);
      });
    } else {
      // plan
      const plan = this.planOrGroup as ConfigurationPlan;
      plan.materialThicknessId = materialThickness.id;
      plan.materialThickness = materialThickness;
      plan.color = null;
      plan.colorId = null;
      this.resetFinishes(plan);
    }

    this.materialThicknessSelected = materialThickness;
    this.colorSelected = undefined;
    await this.preview.recreateEdgesCutawaysAndJambs();
    await this.preview.recreateSplashbacks();
  }

  /**
   * Réinitialise toutes les finitions et crédences d'un plan.
   * À appeler dès que le décor, l'épaisseur ou le matériau change.
   */
  private resetFinishes(plan: ConfigurationPlan): void {
    plan.configurationEdges = [];
    plan.configurationJambs = [];
    plan.configurationCutaways = [];
    plan.configurationCuttings = [];
    (plan as any).configurationSideCuts = [];
    (plan as any).configurationSplashbacks = [];
  }

  /**
   * Sélection du décor
   * @param color
   */
  selectColor(color: Color): void {
    // group
    if (this.planOrGroup instanceof ConfigurationGroup) {
      this.planOrGroup.workbenchConfigurationPlans.forEach(plan => {
        plan.colorId = color.id;
        plan.color = color;
        this.resetFinishes(plan);
        this.preview.loadImage(color.picture.path, plan.displayOrder);
      });
    } else {
      // plan
      const plan = this.planOrGroup as ConfigurationPlan;
      plan.colorId = color.id;
      plan.color = color;
      this.resetFinishes(plan);
      this.preview.loadImage(color.picture.path, plan.displayOrder);
    }

    this.colorSelected = color;
    this.preview.recreateEdgesCutawaysAndJambs();
    this.preview.recreateSplashbacks();
  }

  /**
   * Vérification du matériau sélectionné
   * @param id
   * @returns
   */
  isMaterialSelected(id: number): boolean {
    if (this.planOrGroup instanceof ConfigurationGroup) {
      return this.planOrGroup.workbenchConfigurationPlans.every(plan => plan.materialId === id);
    }

    return this.planOrGroup.materialId === id;
  }

  /**
   * Vérification de l'épaisseur sélectionnée
   * @param id
   * @returns
   */
  isMaterialThicknessSelected(id: number): boolean {
    if (this.planOrGroup instanceof ConfigurationGroup) {
      return this.planOrGroup.workbenchConfigurationPlans.every(plan => plan.materialThicknessId === id);
    }

    return this.planOrGroup.materialThicknessId === id;
  }

  /**
   * Vérification si le décor est sélectionné
   * @param id
   * @returns
   */
  isColorSelected(id: number): boolean {
    if (this.planOrGroup instanceof ConfigurationGroup) {
      return this.planOrGroup.workbenchConfigurationPlans.every(plan => plan.colorId === id);
    }

    return this.planOrGroup.colorId === id;
  }

  /**
   * Dans le cas d'un groupe, on crée un string avec le nom des différents plans du groupe
   */
  getPlanNames(): string {
    let planNames = '';

    if (this.planOrGroup instanceof ConfigurationGroup) {
      planNames = '(';

      for (const [i, plan] of this.planOrGroup.workbenchConfigurationPlans
        .sort((a, b) => a.name.localeCompare(b.name))
        .entries()) {
        planNames += plan.name;

        if (i !== this.planOrGroup.workbenchConfigurationPlans.length - 1) {
          planNames += ', ';
        }
      }

      planNames += ')';
    }

    return planNames;
  }

  /**
   * Récupère le plan
   * @private
   */
  private getConfigurationPlan(): ConfigurationPlan {
    if (this.planOrGroup instanceof ConfigurationGroup) {
      return this.planOrGroup.workbenchConfigurationPlans[0];
    }

    return this.planOrGroup;
  }

  /**
   * Récupère les restrictions du ou des plans
   * @private
   */
  private getPlanRestrictions(): PlanRestriction[] {
    if (this.planOrGroup instanceof ConfigurationGroup) {
      return this.planOrGroup.workbenchConfigurationPlans
        .filter(p => p.planRestrictions != null)
        .map(p => p.planRestrictions);
    }

    return [(this.planOrGroup as ConfigurationPlan).planRestrictions];
  }
}
